import { NamedResource } from '@core/resources/resource';
import { DESTINATION_ICONS, Destination } from '@core/domains/destination/destination';
import { DomainValidators } from '@core/tools/custom.validator';
import { toASCII } from 'punycode/';


export type Domain = string;

export interface DomainListIO {
  id: string;
  name: string;
  description: string;
  domains: Domain[];
  created_at: string | undefined;
  updated_by: string | undefined;
  updated_at: string | undefined;
}

export class DomainList extends NamedResource implements Destination<DomainList> {
  readonly type = 'domain_list';
  readonly icon = DESTINATION_ICONS.domain_list;
  override name: string;
  warning: string[] = [];
  description: string;
  domains: Domain[];
  created_at: Date | undefined;
  updated_by: string | undefined;
  updated_at: Date | undefined;

  constructor(data?: DomainListIO | string) {
    super();
    this.domains = [];
    if (typeof data === 'object') {
      this.id = data?.id ?? undefined;
      this.name = data?.name ?? '';
      this.description = data?.description ?? '';
      if (Array.isArray(data?.domains)) {
        data.domains.forEach((domain) => {
          this.add(domain);
        });
      }
      this.updated_by = data?.updated_by ?? '';
      this.updated_at = data?.updated_at ? new Date(data.updated_at) : undefined;
      this.created_at = data?.created_at ? new Date(data.created_at) : undefined;
    } else {
      this.name = data ?? '';
      this.description = '';
    }
  }

  get length(): number {
    return this.domains.length;
  }

  public setWarning(payload: string): void {
    if (!this.warning.includes(payload)) {
      this.warning.push(payload);
    }
  }

  public clearWarning(): void {
    this.warning = [];
  }

  public getFullName(): string {
    return this.name;
  }

  public add(domain: string): DomainList {
    const cleaned_domain = DomainValidators.clean_domain(domain);
    const errors = DomainValidators.validate_domain(cleaned_domain);
    if (!(errors && Object.keys(errors).length > 0 ) && !this.domains.includes(cleaned_domain)) {
      this.domains.push(cleaned_domain);
    }
    this.domains.sort();

    return this;
  }

  /**
   * This function returns the list of domains, IDNA encoded so that the backend does not need to take care of it.
   * @return any JSON representation of the domain list
   */
  public toJson(): DomainListIO {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      domains: this.domains.map((domain) => toASCII(domain)),
      created_at: this.created_at?.toString() ?? undefined,
      updated_at: this.updated_at?.toString() ?? undefined,
      updated_by: this.updated_by ?? undefined
    };
  }

  clone(): DomainList {
    return new DomainList(this.toJson());
  }

  getCount(): string {
    return this.domains.length.toString();
  }
}
